import Phaser from 'phaser'
import { EVENTS, DamageEvent, LevelUpEvent } from '../types/GameTypes'

interface FloatingText {
  text: Phaser.GameObjects.Text
  velocityY: number
  lifetime: number
  maxLifetime: number
}

export class DamageNumbers {
  private scene: Phaser.Scene
  private floatingTexts: FloatingText[] = []

  constructor(scene: Phaser.Scene) {
    this.scene = scene

    // Listen for damage events
    this.scene.events.on(EVENTS.DAMAGE_DEALT, this.onDamageDealt, this)
    this.scene.events.on(EVENTS.LEVEL_UP, this.onLevelUp, this)
    this.scene.events.on(EVENTS.EXPERIENCE_GAINED, this.onExperienceGained, this)
  }

  private onDamageDealt(event: DamageEvent): void {
    const { damage, isCritical, targetX, targetY, target } = event

    // Determine color based on who is being damaged
    let color = '#ffffff'
    let fontSize = 16

    if (target === 'player') {
      // Player taking damage - red
      color = isCritical ? '#ff0000' : '#ef4444'
      fontSize = isCritical ? 20 : 16
    } else {
      // Creature taking damage - yellow/white
      color = isCritical ? '#ffd700' : '#ffffff'
      fontSize = isCritical ? 20 : 16
    }

    // Add random horizontal offset
    const offsetX = Phaser.Math.Between(-15, 15)
    const offsetY = Phaser.Math.Between(-10, 0)

    this.createFloatingText(
      targetX + offsetX,
      targetY - 20 + offsetY,
      `${isCritical ? 'CRIT! ' : ''}${damage}`,
      color,
      fontSize
    )
  }

  private onLevelUp(event: LevelUpEvent): void {
    // Show level up text at top of screen (UIScene handles this better)
    // But we can show it at player position too
    const player = (this.scene as any).player
    if (player) {
      this.createFloatingText(
        player.x,
        player.y - 40,
        `LEVEL UP! ${event.newLevel}`,
        '#ffd700',
        24,
        2000
      )
    }
  }

  private onExperienceGained(amount: number): void {
    const player = (this.scene as any).player
    if (player) {
      this.createFloatingText(
        player.x + Phaser.Math.Between(-20, 20),
        player.y - 10,
        `+${amount} XP`,
        '#22c55e',
        14,
        1000
      )
    }
  }

  /**
   * Create a floating text that rises and fades
   */
  public createFloatingText(
    x: number,
    y: number,
    message: string,
    color: string,
    fontSize: number = 16,
    lifetime: number = 1500
  ): void {
    const text = this.scene.add.text(x, y, message, {
      fontSize: `${fontSize}px`,
      color: color,
      fontFamily: 'Arial',
      fontStyle: 'bold',
      stroke: '#000000',
      strokeThickness: 3
    })
    text.setOrigin(0.5, 0.5)
    text.setDepth(1000) // Always on top

    const floatingText: FloatingText = {
      text,
      velocityY: -60, // Pixels per second upward
      lifetime: 0,
      maxLifetime: lifetime
    }

    this.floatingTexts.push(floatingText)
  }

  /**
   * Update all floating texts
   */
  public update(delta: number): void {
    const toRemove: FloatingText[] = []

    this.floatingTexts.forEach(ft => {
      ft.lifetime += delta

      // Move upward
      ft.text.y += (ft.velocityY * delta) / 1000

      // Slow down over time
      ft.velocityY *= 0.98

      // Fade out
      const progress = ft.lifetime / ft.maxLifetime
      ft.text.setAlpha(1 - progress)

      // Mark for removal
      if (ft.lifetime >= ft.maxLifetime) {
        toRemove.push(ft)
      }
    })

    // Remove expired texts
    toRemove.forEach(ft => {
      ft.text.destroy()
      const index = this.floatingTexts.indexOf(ft)
      if (index !== -1) {
        this.floatingTexts.splice(index, 1)
      }
    })
  }

  /**
   * Clean up
   */
  public destroy(): void {
    this.scene.events.off(EVENTS.DAMAGE_DEALT, this.onDamageDealt, this)
    this.scene.events.off(EVENTS.LEVEL_UP, this.onLevelUp, this)
    this.scene.events.off(EVENTS.EXPERIENCE_GAINED, this.onExperienceGained, this)

    this.floatingTexts.forEach(ft => ft.text.destroy())
    this.floatingTexts = []
  }
}
